/**
 * useTriggerJob Hook
 *
 * React Query mutation hook for manually triggering an ELT job.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { eltJobService } from "../../../api/elt-job.service";
import type { EltJobTriggerDTO } from "../../../types/elt-job.types";
import type { EltJobExecution } from "../../../types/elt-job-execution.types";
import { getErrorMessage } from "../../../lib/utils/errors";

// Import job executions query key to invalidate after triggering
const JOB_EXECUTIONS_QUERY_KEY = "jobExecutions";

interface UseTriggerJobOptions {
  onSuccess?: (jobExecution: EltJobExecution) => void;
  onError?: (error: string) => void;
}

export function useTriggerJob(tenantId: string, eltJobId: string, options?: UseTriggerJobOptions) {
  const queryClient = useQueryClient();

  return useMutation<EltJobExecution, Error, EltJobTriggerDTO | undefined>({
    mutationFn: (data?: EltJobTriggerDTO) => eltJobService.triggerJob(tenantId, eltJobId, data),
    onSuccess: (jobExecution) => {
      // Invalidate job executions to show the new execution
      queryClient.invalidateQueries({ queryKey: [JOB_EXECUTIONS_QUERY_KEY, tenantId] });
      options?.onSuccess?.(jobExecution);
    },
    onError: (error) => {
      options?.onError?.(getErrorMessage(error));
    },
  });
}
